import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const VotingOptionsContext = createContext(null);

const EMPTY_OPTIONS = {
  site: [],
  fb: [],
  ott: [],
};

const normalizeOptions = (data) => ({
  site: Array.isArray(data?.site) ? data.site : [],
  fb: Array.isArray(data?.fb) ? data.fb : [],
  ott: Array.isArray(data?.ott) ? data.ott : [],
});

const OPTIONS_API_URL = 'https://mca.hot-mob.com/api/voting/options';

export const VotingOptionsProvider = ({ children }) => {
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const fetchOptions = async () => {
      try {
        setIsLoading(true);
        setError('');

        const response = await fetch(OPTIONS_API_URL);
        if (!response.ok) {
          throw new Error(`Failed to load voting options (${response.status})`);
        }

        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          const text = await response.text();
          const preview = text.slice(0, 80).replace(/\s+/g, ' ');
          throw new Error(`API did not return JSON. Response starts with: ${preview}`);
        }

        const data = await response.json();

        if (!isMounted) {
          return;
        }

        setOptions(normalizeOptions(data));
      } catch (err) {
        if (!isMounted) {
          return;
        }
        setError(err?.message || 'Failed to load voting options');
        setOptions(EMPTY_OPTIONS);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  const value = useMemo(
    () => ({
      options,
      isLoading,
      error,
    }),
    [options, isLoading, error]
  );

  return <VotingOptionsContext.Provider value={value}>{children}</VotingOptionsContext.Provider>;
};

export const useVotingOptions = () => {
  const context = useContext(VotingOptionsContext);
  if (!context) {
    throw new Error('useVotingOptions must be used within VotingOptionsProvider');
  }
  return context;
};
